import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export interface GuideStep {
  title: string;
  hint: string;
  done: boolean;
}

interface StepGuideProps {
  mode: string;
  circuitReady: boolean;
  readingCount: number;
  onCheckCircuit: () => void;
}

const StepGuide: React.FC<StepGuideProps> = ({ mode, circuitReady, readingCount, onCheckCircuit }) => {
  const steps = [
    { title: 'Identify the source, load, rheostat and key.', hint: 'Start with the source switched off and check the resistance setting.', done: true },
    { title: 'Connect the ammeter in series with the load.', hint: 'The main current path should pass through the ammeter.', done: circuitReady },
    { title: 'Connect the voltmeter across the resistance wire.', hint: 'Its two leads should touch opposite sides of the measured load.', done: circuitReady },
    { title: 'Close the key and take a reading.', hint: 'Change voltage or resistance in small steps. Open the key before rewiring.', done: readingCount > 0 },
    { title: 'Record at least three sets of observations.', hint: 'Compare V/I for each reading and look for a straight-line relationship.', done: readingCount >= 3 },
  ];
  const modeTitle = {
    verify: 'Mode A · Verify Ohm’s Law',
    length: 'Mode B · Resistance vs length',
    'series-parallel': 'Mode C · Series & parallel',
    sandbox: 'Mode D · Free play',
  }[mode] || 'Guided practical';

  return (
    <Accordion className="ohm-panel" defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Stack direction="row" spacing={1} alignItems="center"><Typography fontWeight={800}>Practical guide</Typography><Chip size="small" label={modeTitle} /></Stack></AccordionSummary>
      <AccordionDetails>
        {mode === 'sandbox' ? <Alert severity="info">Sandbox mode is open-ended. Use Circuit Check before energising, and record observations as you explore.</Alert> : (
          <Stack spacing={1.2}>
            {steps.map((step, index) => <Box key={step.title} className={`ohm-guide-step ${step.done ? 'is-done' : ''}`}>
              <Chip size="small" color={step.done ? 'success' : 'default'} label={step.done ? '✓' : index + 1} />
              <Box><Typography variant="body2" fontWeight={700}>{step.title}</Typography><Typography variant="caption" color="text.secondary">{step.hint}</Typography></Box>
            </Box>)}
          </Stack>
        )}
        <Button size="small" variant="outlined" onClick={onCheckCircuit} sx={{ mt: 2 }}>Show circuit check</Button>
      </AccordionDetails>
    </Accordion>
  );
};

export default StepGuide;
